import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, App as AntApp, Button, Card, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Passenger } from '../../../src/types.ts';
import { api, errorText } from '../lib/api.ts';
import { PassengerForm } from '../components/PassengerForm.tsx';
import { paxType } from '../components/PassengerPicker.tsx';

const TYPE_LABEL: Record<string, string> = { ADT: 'Adult', CHD: 'Child', INF: 'Infant' };

/** Saved travellers — the people a booking can be made for. */
export function PassengersPage() {
  const { message } = AntApp.useApp();
  const [list, setList] = useState<Passenger[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<Passenger | null | 'new'>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .passengers()
      .then((r) => setList(r.items))
      .catch((e) => setError(errorText(e)));
  }, []);
  useEffect(load, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (list ?? []).filter((p) => !q || `${p.firstName} ${p.lastName} ${p.passportNo}`.toLowerCase().includes(q));
  }, [list, query]);

  const remove = async (p: Passenger) => {
    try {
      await api.deletePassenger(p.id);
      message.success('Passenger removed.');
      load();
    } catch (err) {
      message.error(errorText(err), 6);
    }
  };

  return (
    <div className="page">
      <Card
        size="small"
        title={
          <Space>
            <span>Passengers</span>
            {list && <Tag bordered={false}>{list.length}</Tag>}
          </Space>
        }
        extra={
          <Space>
            <Input.Search allowClear size="small" placeholder="Search" value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: 200 }} />
            <Button size="small" icon={<ReloadOutlined />} onClick={load} />
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')}>
              Add passenger
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Names must match the passport exactly. The date of birth decides whether etravel prices the person as an adult, child or infant.
        </Typography.Paragraph>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        <Table<Passenger>
          size="small"
          rowKey="id"
          loading={list === null && !error}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: 'No passengers saved yet.' }}
          columns={[
            { title: 'Name', render: (_: unknown, p) => <strong>{[p.title, p.firstName, p.lastName].filter(Boolean).join(' ')}</strong> },
            {
              title: 'Type',
              width: 90,
              render: (_: unknown, p) => {
                const t = paxType(p);
                return (
                  <Tag bordered={false} color={t === 'ADT' ? 'blue' : t === 'CHD' ? 'purple' : 'magenta'}>
                    {TYPE_LABEL[t] ?? t}
                  </Tag>
                );
              },
            },
            { title: 'Born', dataIndex: 'birthday', width: 120 },
            { title: 'Nationality', dataIndex: 'nationalityCode', width: 110 },
            { title: 'Passport', dataIndex: 'passportNo' },
            {
              title: 'Expires',
              dataIndex: 'passportExpiryDate',
              width: 130,
              render: (v: string) => {
                const soon = v && Date.parse(v) - Date.now() < 183 * 86_400_000;
                return soon ? <Typography.Text type="danger">{v}</Typography.Text> : v;
              },
            },
            { title: 'Issued by', dataIndex: 'passportIssueCode', width: 100 },
            {
              title: '',
              width: 90,
              align: 'right',
              render: (_: unknown, p) => (
                <Space size={4}>
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditing(p)} />
                  <Popconfirm title="Remove this passenger?" description="Passengers used in a booking cannot be removed." onConfirm={() => void remove(p)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal open={editing !== null} onCancel={() => setEditing(null)} footer={null} destroyOnHidden width={720} title={editing === 'new' ? 'New passenger' : 'Edit passenger'}>
        <PassengerForm
          passenger={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      </Modal>
    </div>
  );
}
